import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => (v == null ? '—' : `A$${Math.round(v).toLocaleString()}`);

/** Leadership-only: enter actual CAC (overrides the model) and new-logo / expansion override for one estimate. */
export function CacEditor({ row, onSaved }) {
  const { user } = useAuth();
  const c = row.cac;
  const [actual, setActual] = useState(c.actual_aud ?? '');
  const [note, setNote] = useState(c.note || '');
  const [logo, setLogo] = useState(c.logo_auto ? '' : c.logo);
  const [saving, setSaving] = useState(false);
  const canEdit = ['admin', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/estimates/${row.id}/cac`, { actual_aud: actual === '' ? null : Number(actual), note: note || null, logo_override: logo || null }, { withCredentials: true });
      toast.success(actual === '' ? 'Actual CAC cleared — back to the modelled value' : `Actual CAC ${fmtA(Number(actual))} saved`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  return (
    <div className="text-[11px] space-y-1 min-w-[260px]" data-testid={`cac-editor-${row.id}`}>
      <div className="text-slate-500">CAC <b className="text-[#0F2040]">{fmtA(c.effective_aud)}</b> ({c.source}) · {c.logo === 'expansion' ? 'expansion' : 'new logo'}{c.logo_auto ? ' (auto)' : ' (override)'}</div>
      <div className="text-slate-400">Modelled {fmtA(c.modelled_aud)} = base {fmtA(c.base_aud)} × {c.type_label} {c.factors.type} × {c.complexity_label} {c.factors.complexity} × {c.region || 'region n/a'} {c.factors.region}{c.factors.expansion !== 1 ? ` × expansion ${c.factors.expansion}` : ''}</div>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <input type="number" min="0" step="500" value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Actual CAC (A$)" className="h-7 w-32 rounded border border-slate-200 px-2 text-[11px]" data-testid={`cac-actual-${row.id}`} />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Source / note" className="h-7 w-44 rounded border border-slate-200 px-2 text-[11px]" data-testid={`cac-note-${row.id}`} />
          <select value={logo} onChange={(e) => setLogo(e.target.value)} className="h-7 rounded border border-slate-200 px-1 text-[11px]" data-testid={`cac-logo-${row.id}`}>
            <option value="">Logo: auto</option><option value="new">New logo</option><option value="expansion">Expansion</option>
          </select>
          <button type="button" onClick={save} disabled={saving} className="h-7 inline-flex items-center gap-1 rounded bg-[#0F2040] px-2 text-white text-[11px] hover:bg-[#1a3260] disabled:opacity-50" data-testid={`cac-save-${row.id}`}><Save className="w-3 h-3" /> Save</button>
        </div>
      ) : c.note ? <div className="text-slate-500">Note: {c.note}</div> : null}
      {c.actual_by && <div className="text-slate-400">Actual entered by {c.actual_by} · {String(c.actual_at).slice(0, 10)}{c.note ? ` · ${c.note}` : ''}</div>}
    </div>
  );
}
